"use client";

import React, { PropsWithChildren, useEffect } from "react";
import Sidebar from "@/components/common/Sidebar/Sidebar";
import { usePathname, useRouter } from "next/navigation";
import BottomNav from "@/components/common/BottomNav/BottomNav";
import { useSubscription } from "@apollo/client";
import { NEW_NOTIFICATION } from "@/graphql/notifications";
import { useAppDispatch, useAppSelector } from "@/lib/store/hooks";
import {
  selectToken,
  selectUserId,
} from "@/lib/store/features/token/tokenSlice";
import { showAlert } from "@/lib/store/features/alert/alertSlice";
import { incrementIsNotRead } from "@/lib/store/features/notifications/notificationSlice";
import { getAuthToken } from "@/utils/authToken";
import { useAuthContext } from "@/contexts/AuthContext";
import useLang from "@/hooks/useLang";

const protectedPaths = ["/inbox", "/notifications", "/payment", "/profile/me"];

const ClientLayout = ({ children }: PropsWithChildren) => {
  const { getText } = useLang();
  const { isAuthenticated } = useAuthContext();
  const pathname = usePathname();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const userToken = useAppSelector(selectToken);
  const userId = useAppSelector(selectUserId);
  const pathOnMessage = pathname?.startsWith("/inbox");

  // Authentication check
  useEffect(() => {
    const token = getAuthToken();
    const isProtectedPath = protectedPaths.some((path) =>
      pathname?.startsWith(path)
    );
    const isProfilePath = pathname === "/profile/me";

    if (!isAuthenticated && !token && isProtectedPath && !isProfilePath) {
      dispatch(
        showAlert({
          visible: true,
          message: getText({ id: "signinAlert" }),
          type: "warning",
        })
      );
      router.push(
        `/auth/signin?callbackUrl=${encodeURIComponent(pathname || "/")}`
      );
    } else if (!isAuthenticated && !token && isProfilePath) {
      router.push(
        `/auth/signin?callbackUrl=${encodeURIComponent(pathname || "/")}`
      );
    }
  }, [isAuthenticated, pathname, dispatch, router, getText]);

  // Notification subscription
  useSubscription(NEW_NOTIFICATION, {
    skip: !userId || !isAuthenticated || !userToken,
    variables: { receiverId: userId },
    onData: ({ client, data }) => {
      const newNotification = data.data?.newNotification;

      if (newNotification && userId) {
        dispatch(
          showAlert({
            visible: true,
            message: getText({ id: "notificationAlert" }),
            type: "info",
            isIntl: true,
          })
        );
        dispatch(incrementIsNotRead());

        client.cache.modify({
          id: client.cache.identify({
            __typename: "User",
            id: userId,
          }),
          fields: {
            countNotification(existing = 0) {
              return existing + 1;
            },
            Notifications(existing = [], { toReference }) {
              return [toReference(newNotification, true), ...existing];
            },
            countNewNotification(existing = 0) {
              return existing + 1;
            },
          },
        });
      }
    },
    onError: (error) => {
      console.error("Subscription error:", error);
    },
  });

  if (
    !isAuthenticated &&
    protectedPaths.some((path) => pathname?.startsWith(path))
  ) {
    return null;
  }

  return (
    <div
      className={`flex overflow-hidden ${
        pathOnMessage ? "space-x-0 ml-0" : "space-x-0 md:space-x-3"
      }`}
    >
      <Sidebar />
      <div className="flex-1">{children}</div>
      {!pathOnMessage && <BottomNav />}
    </div>
  );
};

export default ClientLayout;
